import React, { createContext, useState, useContext } from 'react';
import { useTranslation } from "react-i18next";

// Define the shape of the ProjectContext.
interface ProjectContextType {
    selectedProjectId: null | number | string;
    setSelectedProjectId: (id: string | null | number) => void;
    selectedProjectName: string;
    setSelectedProjectName: (name: string) => void;
    selectedProjectItem: { id: number | string | null; name: string | null };
    setSelectedProjectItem: (item: { id: number | string | null; name: string | null }) => void;
    clearProject: () => void;
}

// Create the context with a default undefined value.
const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

// Create the provider component.
export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { t } = useTranslation();
    const [selectedProjectId, setSelectedProjectId] = useState<number | string | null>(null);
    const [selectedProjectName, setSelectedProjectName] = useState<string>(t("common.All"));
    const [selectedProjectItem, setSelectedProjectItem] = useState<{ id: number | string | null; name: string | null }>({
        id: null,
        name: t("common.All"),
    });
    // Function to clear all selected states.
    const clearProject = () => {
        setSelectedProjectId(null);
        setSelectedProjectName('All');
        setSelectedProjectItem({ id: null, name: 'All' });
    };

    return (
        <ProjectContext.Provider
            value={{
                selectedProjectId,
                setSelectedProjectId,
                selectedProjectName,
                setSelectedProjectName,
                setSelectedProjectItem,
                selectedProjectItem,
                clearProject,
            }}
        >
            {children}
        </ProjectContext.Provider>
    );
};

// Hook to use the ProjectContext.
export const useProjectContext = () => {
    const context = useContext(ProjectContext);
    if (context === undefined) {
        throw new Error('useProjectContext must be used within a ProjectProvider');
    }
    return context;
};
